import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Row, Button } from 'react-bootstrap';
import { navigate } from 'raviger';
import ReactQuill from 'react-quill';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import withForm from '../../hoc/withForm';
import Permission from '../../components/Permission/Permission';

import { useAlertStore } from '../../store/providers/Alert';
import { useGlobalSettingsStore } from '../../store/providers/GlobalSettings';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';

import validations from './validations';

const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const InputCollection = React.lazy(() => import('../../components/InputCollection/InputCollection'));

function GlobalSettings(props) {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, updateRecord, data, clearData } = useGlobalSettingsStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isBusy, setIsBusy] = useState(false);

    const pagePermission = constants.PERMISSION_SETTINGS_GLOBALSETTINGS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const submittedHandler = (e) => {
        e.preventDefault();

        var valid = props.formValidate(validations);

        if (valid) {
            props.formSubmitted();
            updateRecord({ ...props.formData, franchiseNewsId: props.formData.id });
        }
    };

    useEffect(() => {
        if (props.formClosing) { navigate('/dashboard'); }
    }, [props]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (!isLoaded && data.status === 'success' && data.scope === 'select') {
                props.formInitialize(data.body);
                setIsLoaded(true);
            }

            if (isLoaded && data.status === 'success' && data.scope === 'update') {
                navigate('/dashboard');
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'select' || data.scope === 'update'));
        }
    }, [data, addAlert, clearData, isLoaded, props]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'globalSettings', parent: 'Settings', text: 'Global Settings' }));

        selectRecord();

        setIsMounted(true);
    }, [isMounted, hasPermission, dispatch, selectRecord]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <>
                        <PageHeader title="Global Settings" />
                        {
                            !isLoaded && <Spinner text="Loading Global Settings..." />
                        }
                        {
                            isLoaded &&
                            <Form onSubmit={(e) => submittedHandler(e)}>
                                <Row>
                                    <div className="col-md-12">
                                        <InputCollection labelText="Terms of Use Text" errorMessage={props.formValidationCheck('termsOfUseText')}>
                                            <ReactQuill id="termsOfUseText" onChange={(e) => props.formChanged('termsOfUseText', e)} defaultValue={props.formData['termsOfUseText'] || ''} readOnly={isBusy}
                                                modules={{
                                                    toolbar: [
                                                        [{ 'font': [] }, { 'header': [1, 2, 3, 4, 5, 6, false] }, { 'size': ['small', false, 'large', 'huge'] }],
                                                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                                        [{ 'background': [] }, { 'color': [] }],
                                                        [{ 'align': [] }],
                                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                                                        ['link', 'image', 'video'],
                                                        ['clean']
                                                    ]
                                                }}
                                                formats={['font', 'header', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'align', 'background', 'color',
                                                    'list', 'bullet', 'indent', 'link', 'image', 'video']}
                                            />
                                        </InputCollection>
                                    </div>
                                </Row>
                                <Row>
                                    <div className="col-md-12">
                                        <InputCollection labelText="Privacy Policy Text" errorMessage={props.formValidationCheck('privacyPolicyText')}>
                                            <ReactQuill id="privacyPolicyText" onChange={(e) => props.formChanged('privacyPolicyText', e)} defaultValue={props.formData['privacyPolicyText'] || ''} readOnly={isBusy}
                                                modules={{
                                                    toolbar: [
                                                        [{ 'font': [] }, { 'header': [1, 2, 3, 4, 5, 6, false] }, { 'size': ['small', false, 'large', 'huge'] }],
                                                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                                        [{ 'background': [] }, { 'color': [] }],
                                                        [{ 'align': [] }],
                                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                                                        ['link', 'image', 'video'],
                                                        ['clean']
                                                    ]
                                                }}
                                                formats={['font', 'header', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'align', 'background', 'color',
                                                    'list', 'bullet', 'indent', 'link', 'image', 'video']}
                                            />
                                        </InputCollection>
                                    </div>
                                </Row>
                                <Row>
                                    <div className="col-md-12">
                                        <InputCollection labelText="CCPA Text" errorMessage={props.formValidationCheck('ccpaText')}>
                                            <ReactQuill id="ccpaText" onChange={(e) => props.formChanged('ccpaText', e)} defaultValue={props.formData['ccpaText'] || ''} readOnly={isBusy}
                                                modules={{
                                                    toolbar: [
                                                        [{ 'font': [] }, { 'header': [1, 2, 3, 4, 5, 6, false] }, { 'size': ['small', false, 'large', 'huge'] }],
                                                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                                        [{ 'background': [] }, { 'color': [] }],
                                                        [{ 'align': [] }],
                                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                                                        ['link', 'image', 'video'],
                                                        ['clean']
                                                    ]
                                                }}
                                                formats={['font', 'header', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'align', 'background', 'color',
                                                    'list', 'bullet', 'indent', 'link', 'image', 'video']}
                                            />
                                        </InputCollection>
                                    </div>
                                </Row>
                                <Row>
                                    <div className="col-md-12">
                                        <InputCollection labelText="DMCA Text" errorMessage={props.formValidationCheck('dmcaText')}>
                                            <ReactQuill id="dmcaText" onChange={(e) => props.formChanged('dmcaText', e)} defaultValue={props.formData['dmcaText'] || ''} readOnly={isBusy}
                                                modules={{
                                                    toolbar: [
                                                        [{ 'font': [] }, { 'header': [1, 2, 3, 4, 5, 6, false] }, { 'size': ['small', false, 'large', 'huge'] }],
                                                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                                        [{ 'background': [] }, { 'color': [] }],
                                                        [{ 'align': [] }],
                                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                                                        ['link', 'image', 'video'],
                                                        ['clean']
                                                    ]
                                                }}
                                                formats={['font', 'header', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'align', 'background', 'color',
                                                    'list', 'bullet', 'indent', 'link', 'image', 'video']}
                                            />
                                        </InputCollection>
                                    </div>
                                </Row>
                                <Row>
                                    <div className="col-md-12">
                                        <InputCollection labelText="Accessibility Text" errorMessage={props.formValidationCheck('accessibilityText')}>
                                            <ReactQuill id="accessibilityText" onChange={(e) => props.formChanged('accessibilityText', e)} defaultValue={props.formData['accessibilityText'] || ''} readOnly={isBusy}
                                                modules={{
                                                    toolbar: [
                                                        [{ 'font': [] }, { 'header': [1, 2, 3, 4, 5, 6, false] }, { 'size': ['small', false, 'large', 'huge'] }],
                                                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                                        [{ 'background': [] }, { 'color': [] }],
                                                        [{ 'align': [] }],
                                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                                                        ['link', 'image', 'video'],
                                                        ['clean']
                                                    ]
                                                }}
                                                formats={['font', 'header', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'align', 'background', 'color',
                                                    'list', 'bullet', 'indent', 'link', 'image', 'video']}
                                            />
                                        </InputCollection>
                                    </div>
                                </Row>
                                <hr className="form-divider" />
                                <div className="form-action-buttons">
                                    <Button bsStyle="default" onClick={() => props.formCancelled()} className="button-group-margin" disabled={isBusy}>
                                        <i className="fa fa-undo button-icon"></i>
                                        Back
                                    </Button>
                                    <Button bsStyle="danger" variant="danger" type="submit" className="button-group-margin" disabled={!canEdit || isBusy}>
                                        <i className="fa fa-save button-icon"></i>
                                        {isBusy ? 'Saving...' : 'Save & Exit'}
                                    </Button>
                                </div>
                            </Form>
                        }
                    </>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(withForm(GlobalSettings)));
